import React, { useState, useEffect } from "react";
import { formatPhoneNumber, formatPhoneNumberInput } from "../../utils/format";

const SettingsTab = ({ mallId, shopNo, showToastMessage, API_URL }) => {
  const [notificationEmail, setNotificationEmail] = useState("");
  const [notificationType, setNotificationType] = useState("email"); // 'email' 또는 'sms'
  const [smsSenderNo, setSmsSenderNo] = useState("");
  const [smsRecipient, setSmsRecipient] = useState("");
  const [smsSenders, setSmsSenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendersLoading, setSendersLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // 알림 설정 불러오기
  const fetchNotificationSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/setting/notification/?mall_id=${mallId}&shop_no=${shopNo}`
      );
      if (!response.ok) {
        throw new Error("알림 설정 정보를 불러오는데 실패했습니다.");
      }
      const result = await response.json();
      const data = result.data || {};

      setNotificationEmail(data.email || "");
      setNotificationType(data.type || "email");
      setSmsSenderNo(data.sms_sender_no || "");
      setSmsRecipient(data.sms_recipient || "");
    } catch (err) {
      console.error("알림 설정 불러오기 실패:", err);
      showToastMessage(`알림 설정 불러오기 실패: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // SMS 발신번호 목록 불러오기
  const fetchSmsSenders = async () => {
    setSendersLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/sms-senders/?mall_id=${mallId}&shop_no=${shopNo}`
      );
      if (!response.ok) {
        throw new Error("SMS 발신번호 목록을 불러오는데 실패했습니다.");
      }
      const data = await response.json();
      // auth_status가 "30"인 발신번호만 필터링
      const approvedSenders =
        data.senders?.filter((sender) => sender.auth_status === "30") || [];
      setSmsSenders(approvedSenders);
    } catch (err) {
      console.error("SMS 발신번호 불러오기 실패:", err);
      showToastMessage(`SMS 발신번호 불러오기 실패: ${err.message}`, "error");
    } finally {
      setSendersLoading(false);
    }
  };

  // 알림 설정 저장하기
  const saveNotificationSettings = async () => {
    // 유효성 검사
    if (notificationType === "email") {
      if (!notificationEmail.trim()) {
        showToastMessage("이메일을 입력해주세요.", "error");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(notificationEmail.trim())) {
        showToastMessage("올바른 이메일 형식을 입력해주세요.", "error");
        return;
      }
    } else if (notificationType === "sms") {
      if (!smsSenderNo) {
        showToastMessage("SMS 발신번호를 선택해주세요.", "error");
        return;
      }
      if (!smsRecipient.trim()) {
        showToastMessage("SMS 수신번호를 입력해주세요.", "error");
        return;
      }
      const phoneRegex = /^01[0-9]-?\d{3,4}-?\d{4}$/;
      if (!phoneRegex.test(smsRecipient.replace(/-/g, ""))) {
        showToastMessage("올바른 휴대폰 번호 형식을 입력해주세요.", "error");
        return;
      }
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/setting/notification/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mall_id: mallId,
          shop_no: shopNo,
          type: notificationType,
          email: notificationEmail.trim(),
          sms_sender_no: smsSenderNo,
          sms_recipient: smsRecipient.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error("알림 설정 저장에 실패했습니다.");
      }

      showToastMessage("알림 설정이 저장되었습니다.", "success");
    } catch (err) {
      console.error("알림 설정 저장 실패:", err);
      showToastMessage(`알림 설정 저장 실패: ${err.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 불러오기
  useEffect(() => {
    if (mallId && shopNo) {
      fetchNotificationSettings();
      fetchSmsSenders();
    }
  }, [mallId, shopNo]);

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".group-wrap")) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const selectedSender = smsSenders.find(
    (sender) => sender.sender_no === smsSenderNo
  );

  return (
    <>
      <style>
        {`
          /* 토글 스위치 스타일 */
          .toggle-switch {
            position: relative;
            display: inline-block;
            width: 60px;
            height: 34px;
          }

          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }

          .toggle-slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #ccc;
            transition: .4s;
            border-radius: 34px;
          }

          .toggle-slider:before {
            position: absolute;
            content: "";
            height: 26px;
            width: 26px;
            left: 4px;
            bottom: 4px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
          }

          input:checked + .toggle-slider {
            background-color: #4A90E2;
          }

          input:checked + .toggle-slider:before {
            transform: translateX(26px);
          }

          /* 콤보박스 스타일 */
          .group-wrap {
            position: relative;
          }

          .group-select {
            all: unset;
            min-width: 200px;
            padding: 10px 16px;
            border-radius: 40px;
            // background: #F5FAFF;
            background: #fff;
            font: 600 16px/1 'Pretendard','Noto Sans KR',sans-serif;
            color: #0170FF;
            cursor: pointer;
            box-shadow: 0 0 0 1px #CFE5FF inset;
            transition: box-shadow .2s;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .group-select.open {
            box-shadow: 0 0 0 2px #34A3FF inset;
          }

          .group-select::after {
            content: "▼";
            font-size: 12px;
            transition: transform 0.2s;
          }

          .group-select.open::after {
            transform: rotate(180deg);
          }

          .group-options {
            position: absolute;
            top: calc(100% + 4px);
            left: 0;
            right: 0;
            padding: 8px 0;
            list-style: none;
            background: #fff;
            border-radius: 16px;
            box-shadow: 0 6px 20px rgba(0,0,0,.08);
            z-index: 50;
            max-height: 150px;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            visibility: hidden;
            opacity: 0;
            transform: translateY(-8px);
            transition: opacity .15s ease, transform .15s ease;
            pointer-events: none;
            margin-block-start: 0;
          }

          .group-options.open {
            visibility: visible;
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }

          .group-options li {
            padding: 10px 20px;
            font-size: 15px;
            cursor: pointer;
            color: #222;
            transition: background .15s;
          }

          .group-options li:hover {
            background: #F0F7FF;
          }

          .group-options li.selected {
            color: #0170FF;
            font-weight: 600;
          }
        `}
      </style>

      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "30px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
          }}
        >
          <h2
            style={{
              margin: "0",
              fontSize: "24px",
              color: "#333",
              fontWeight: "600",
            }}
          >
            알림설정
          </h2>

          {/* 저장 버튼 */}
          <button
            onClick={saveNotificationSettings}
            disabled={saving}
            style={{
              padding: "10px 20px",
              backgroundColor: saving ? "#9ca3af" : "#4A90E2",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
              transition: "background-color 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.target.style.backgroundColor = "#357ABD";
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.target.style.backgroundColor = "#4A90E2";
              }
            }}
          >
            {saving ? (
              <>
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    border: "2px solid transparent",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                저장 중...
              </>
            ) : (
              <>저장</>
            )}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #4A90E2",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <p style={{ color: "#666" }}>설정을 불러오는 중...</p>
          </div>
        ) : (
          <div
            style={{ display: "flex", gap: "56px", alignItems: "flex-start" }}
          >
            {/* 왼쪽: 알림 방식 선택 */}
            <div
              style={{
                width: "380px",
                flexShrink: 0,
                padding: "24px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                backgroundColor: "#f8fafc",
              }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                🔔 회원가입 알림 방식 선택
              </h3>

              {/* 이메일 알림 */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  padding: "16px",
                  backgroundColor:
                    notificationType === "email" ? "#e0f2fe" : "white",
                  borderRadius: "8px",
                  border: `2px solid ${
                    notificationType === "email" ? "#4A90E2" : "#e2e8f0"
                  }`,
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <span style={{ fontSize: "20px" }}>📧</span>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    이메일로 받기
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationType === "email"}
                    onChange={(e) =>
                      setNotificationType(e.target.checked ? "email" : "sms")
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {/* SMS 알림 */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  backgroundColor:
                    notificationType === "sms" ? "#e0f2fe" : "white",
                  borderRadius: "8px",
                  border: `2px solid ${
                    notificationType === "sms" ? "#4A90E2" : "#e2e8f0"
                  }`,
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <span style={{ fontSize: "20px" }}>💬</span>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    SMS로 받기
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationType === "sms"}
                    onChange={(e) =>
                      setNotificationType(e.target.checked ? "sms" : "email")
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* 오른쪽: 설정 및 저장 영역 */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* 이메일 설정 */}
              {notificationType === "email" && (
                <div
                  style={{
                    marginBottom: "30px",
                    padding: "24px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 16px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    📧 이메일 설정
                  </h3>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      수신이메일 주소
                    </label>
                    <input
                      type="email"
                      value={notificationEmail}
                      onChange={(e) => setNotificationEmail(e.target.value)}
                      placeholder="example@domain.com"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: "white",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#4A90E2";
                        e.target.style.outline = "none";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                      }}
                    />
                  </div>
                </div>
              )}

              {/* SMS 설정 */}
              {notificationType === "sms" && (
                <div
                  style={{
                    marginBottom: "30px",
                    padding: "24px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 16px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    💬 SMS 설정
                  </h3>

                  {/* SMS 발신번호 선택 */}
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      발신번호
                    </label>
                    <div className="group-wrap">
                      <div
                        className={`group-select ${dropdownOpen ? "open" : ""}`}
                        onClick={() =>
                          !sendersLoading && setDropdownOpen(!dropdownOpen)
                        }
                        style={{
                          cursor: sendersLoading ? "not-allowed" : "pointer",
                          opacity: sendersLoading ? 0.7 : 1,
                        }}
                      >
                        {sendersLoading ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                border: "2px solid #CFE5FF",
                                borderTop: "2px solid #0170FF",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            ></div>
                            {/* 발신번호 불러오는 중... */}
                          </div>
                        ) : selectedSender ? (
                          formatPhoneNumber(selectedSender.sender)
                        ) : (
                          "발신번호를 선택하세요"
                        )}
                      </div>
                      <ul
                        className={`group-options ${
                          dropdownOpen ? "open" : ""
                        }`}
                      >
                        {smsSenders.map((sender) => (
                          <li
                            key={sender.sender_no}
                            className={
                              smsSenderNo === sender.sender_no ? "selected" : ""
                            }
                            onClick={() => {
                              setSmsSenderNo(sender.sender_no);
                              setDropdownOpen(false);
                            }}
                          >
                            {formatPhoneNumber(sender.sender)}
                          </li>
                        ))}
                        {smsSenders.length === 0 && (
                          <li style={{ color: "#6b7280", fontStyle: "italic" }}>
                            승인된 발신번호가 없습니다
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

                  {/* SMS 수신번호 입력 */}
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      수신번호
                    </label>
                    <input
                      type="tel"
                      value={smsRecipient}
                      onChange={(e) =>
                        setSmsRecipient(formatPhoneNumberInput(e.target.value))
                      }
                      placeholder="010-1234-5678"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: "white",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#4A90E2";
                        e.target.style.outline = "none";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                      }}
                    />
                  </div>
                </div>
              )}

              {/* 설정 안내 */}
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#f0f9ff",
                  borderRadius: "8px",
                  border: "1px solid #bfdbfe",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 12px 0",
                    color: "#1e40af",
                    fontSize: "16px",
                  }}
                >
                  💡 설정 안내
                </h4>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "20px",
                    color: "#1e40af",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  <li>이메일 또는 SMS 중 하나의 방식을 선택할 수 있습니다</li>
                  <li>
                    SMS 발신번호는 CAFE24에서 승인된 번호만 사용 가능합니다
                  </li>
                  <li>회원가입 신청 시 선택한 방식으로 알림이 발송됩니다</li>
                  <li>설정은 즉시 적용됩니다</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SettingsTab;
